import * as React from "react";
import {
  SCRIPTS_BROADCAST_GUIDELINE_RULES_MAX,
  SCRIPTS_BROADCAST_GUIDELINES_MAX_CHARS,
  SCRIPTS_BROADCAST_SET_NAME_MAX_CHARS,
  SCRIPTS_BROADCAST_SCRIPTS_MAX,
  SCRIPTS_BROADCAST_TENANTS_MAX,
  type ScriptsBroadcastGuidelineRule,
  SetNameSchema,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
  SegmentedControl,
  Textarea,
  cn,
} from "@nexus/ui";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { OrgPickerOptionContent } from "@/features/identity-hub/components/dashboard/org-picker-shared";
import { AdminOrdersMultiSelect } from "@/features/marketplace/admin/components/AdminOrdersMultiSelect";
import {
  BROADCAST_PAIR_DESCRIPTION_MAX_CHARS,
  findOversizedPair,
  pruneGuidelineRules,
} from "@/features/persona-opportunities/lib/scripts-broadcast-guideline-rules";
import { defaultBroadcastSetName } from "@/features/persona-opportunities/lib/scripts-broadcast-set-name";
import { getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import {
  broadcastStartedNothing,
  summarizeScriptsBroadcast,
  tenantPickerQueryOptions,
  useBroadcastScripts,
} from "@/lib/queries";

export interface BroadcastScript {
  id: string;
  title: string;
  /**
   * Estimated length of this script's job description before guidelines
   * (`estimateBroadcastDescriptionLength`). Without it the per-pair size check
   * skips the script and the BFF's 400 is the only check.
   */
  descriptionLength?: number;
}

export interface BroadcastScriptsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selected platform-tenant scripts. Each one becomes one adapted script per tenant. */
  scripts: BroadcastScript[];
  /**
   * Name of the set the selected scripts came from (the first selected
   * script's set when they span several). Default name of the new set.
   */
  sourceSetName?: string | null;
  /** Runs after the BFF accepted the run (clear the selection here). */
  onStarted?: () => void;
  /** Clock for the fallback set name. Tests pass a fixed date. */
  now?: Date;
}

/** Tenants that can receive a set: client orgs, never Adge itself or a production partner. */
const EXCLUDED_TENANT_TYPES = new Set(["SUPER_TENANT", "PARTNER"]);

/** Tenant names shown on a rule before the rest collapse into "+n". */
const RULE_TENANT_NAMES_SHOWN = 3;

/** Past this a rule's text may not fit two lines, so it gets a Show more toggle. */
const RULE_TEXT_CLAMP_HINT_CHARS = 140;

type RuleScope = "all" | "choose";

interface RuleDraft {
  /**
   * Index of the rule being edited; null for a new rule. The form reads only
   * its initial draft, so the dialog keeps this one current when rules shift.
   */
  index: number | null;
  text: string;
  scriptScope: RuleScope;
  scriptIds: string[];
  tenantScope: RuleScope;
  tenantIds: string[];
}

interface ScopeItem {
  id: string;
  label: string;
}

function emptyDraft(): RuleDraft {
  return {
    index: null,
    text: "",
    scriptScope: "all",
    scriptIds: [],
    tenantScope: "all",
    tenantIds: [],
  };
}

function draftFromRule(rule: ScriptsBroadcastGuidelineRule, index: number): RuleDraft {
  return {
    index,
    text: rule.text,
    scriptScope: rule.scriptIds ? "choose" : "all",
    scriptIds: rule.scriptIds ?? [],
    tenantScope: rule.tenantIds ? "choose" : "all",
    tenantIds: rule.tenantIds ?? [],
  };
}

function formatSetNameMonth(now: Date): string {
  // English on purpose: the set name is shared with every receiving tenant.
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(now);
}

function ScopeChips({
  items,
  selectedIds,
  onChange,
  disabled,
  ariaLabel,
}: {
  items: ScopeItem[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  const selected = new Set(selectedIds);
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {items.map((item) => {
        const checked = selected.has(item.id);
        return (
          <label
            key={item.id}
            className={cn(
              "inline-flex h-7 max-w-full cursor-pointer items-center gap-1.5 rounded-sm border px-2 text-xs transition-colors",
              checked
                ? "border-primary/40 bg-primary/5 text-foreground dark:border-primary/60 dark:bg-primary/15 dark:text-primary"
                : "border-border bg-background text-foreground hover:bg-muted",
              disabled && "cursor-not-allowed opacity-40",
            )}
          >
            <Checkbox
              checked={checked}
              disabled={disabled}
              onCheckedChange={(next) =>
                onChange(
                  next === true
                    ? items
                        .filter((entry) => selected.has(entry.id) || entry.id === item.id)
                        .map((entry) => entry.id)
                    : selectedIds.filter((id) => id !== item.id),
                )
              }
            />
            <span className="truncate">{item.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function GuidelineRuleForm({
  initial,
  scriptItems,
  tenantItems,
  disabled,
  onSave,
  onCancel,
}: {
  initial: RuleDraft;
  scriptItems: ScopeItem[];
  tenantItems: ScopeItem[];
  disabled: boolean;
  onSave: (rule: ScriptsBroadcastGuidelineRule) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState(initial);

  // Ids that left the dialog while the form was open do not count.
  const scriptIds = draft.scriptIds.filter((id) =>
    scriptItems.some((item) => item.id === id),
  );
  const tenantIds = draft.tenantIds.filter((id) => tenantItems.some((item) => item.id === id));
  const text = draft.text.trim();
  const missingScripts = draft.scriptScope === "choose" && scriptIds.length === 0;
  const missingTenants = draft.tenantScope === "choose" && tenantIds.length === 0;
  const canSave = !disabled && text.length > 0 && !missingScripts && !missingTenants;

  function handleSave() {
    if (!canSave) return;
    onSave({
      text,
      ...(draft.scriptScope === "choose" ? { scriptIds } : {}),
      ...(draft.tenantScope === "choose" ? { tenantIds } : {}),
    });
  }

  const scopeOptions = [
    { value: "all", label: t("personaOpportunities.scripts.broadcast.ruleScopeAll", "All") },
    {
      value: "choose",
      label: t("personaOpportunities.scripts.broadcast.ruleScopeChoose", "Choose"),
    },
  ];

  return (
    <div
      className="flex flex-col gap-3 rounded-md border border-border bg-muted/40 p-3"
      data-testid="persona-broadcast-rule-form"
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="persona-broadcast-rule-text">
            {draft.index === null
              ? t("personaOpportunities.scripts.broadcast.ruleFormNew", "New guideline")
              : t("personaOpportunities.scripts.broadcast.ruleFormEdit", "Edit guideline")}
          </Label>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {t("personaOpportunities.scripts.broadcast.guidelinesCount", {
              defaultValue: "{{count}} / {{max}}",
              count: draft.text.length,
              max: SCRIPTS_BROADCAST_GUIDELINES_MAX_CHARS,
            })}
          </span>
        </div>
        <Textarea
          id="persona-broadcast-rule-text"
          value={draft.text}
          rows={3}
          autoFocus
          maxLength={SCRIPTS_BROADCAST_GUIDELINES_MAX_CHARS}
          disabled={disabled}
          placeholder={t(
            "personaOpportunities.scripts.broadcast.ruleTextPlaceholder",
            "For example: keep every script under 30 seconds.",
          )}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              text: event.target.value.slice(0, SCRIPTS_BROADCAST_GUIDELINES_MAX_CHARS),
            }))
          }
          className="resize-none bg-background"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-3">
          <span className="w-16 shrink-0 text-xs text-muted-foreground">
            {t("personaOpportunities.scripts.broadcast.scriptsLabel", "Scripts")}
          </span>
          <SegmentedControl
            value={draft.scriptScope}
            disabled={disabled}
            accent="primary"
            tone="subtle"
            ariaLabel={t(
              "personaOpportunities.scripts.broadcast.ruleScriptsScopeAria",
              "Scripts this guideline applies to",
            )}
            onValueChange={(value) => {
              if (value === "all" || value === "choose") {
                setDraft((current) => ({ ...current, scriptScope: value }));
              }
            }}
            options={scopeOptions}
          />
        </div>
        {draft.scriptScope === "choose" ? (
          <>
            <ScopeChips
              items={scriptItems}
              selectedIds={scriptIds}
              disabled={disabled}
              ariaLabel={t(
                "personaOpportunities.scripts.broadcast.ruleScriptsPick",
                "Pick scripts",
              )}
              onChange={(ids) => setDraft((current) => ({ ...current, scriptIds: ids }))}
            />
            {missingScripts ? (
              <p className="text-xs text-muted-foreground">
                {t(
                  "personaOpportunities.scripts.broadcast.ruleNeedsScript",
                  "Pick at least one script.",
                )}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-3">
          <span className="w-16 shrink-0 text-xs text-muted-foreground">
            {t("personaOpportunities.scripts.broadcast.tenantsLabel", "Tenants")}
          </span>
          <SegmentedControl
            value={draft.tenantScope}
            disabled={disabled}
            accent="primary"
            tone="subtle"
            ariaLabel={t(
              "personaOpportunities.scripts.broadcast.ruleTenantsScopeAria",
              "Tenants this guideline applies to",
            )}
            onValueChange={(value) => {
              if (value === "all" || value === "choose") {
                setDraft((current) => ({ ...current, tenantScope: value }));
              }
            }}
            options={scopeOptions}
          />
        </div>
        {draft.tenantScope === "choose" ? (
          <>
            {tenantItems.length > 0 ? (
              <ScopeChips
                items={tenantItems}
                selectedIds={tenantIds}
                disabled={disabled}
                ariaLabel={t(
                  "personaOpportunities.scripts.broadcast.ruleTenantsPick",
                  "Pick tenants",
                )}
                onChange={(ids) => setDraft((current) => ({ ...current, tenantIds: ids }))}
              />
            ) : null}
            {missingTenants ? (
              <p className="text-xs text-muted-foreground">
                {tenantItems.length > 0
                  ? t(
                      "personaOpportunities.scripts.broadcast.ruleNeedsTenant",
                      "Pick at least one tenant.",
                    )
                  : t(
                      "personaOpportunities.scripts.broadcast.ruleNoTenants",
                      "Pick tenants above first.",
                    )}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="xs" disabled={disabled} onClick={onCancel}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          type="button"
          size="xs"
          disabled={!canSave}
          onClick={handleSave}
          data-testid="persona-broadcast-rule-save"
        >
          {t("personaOpportunities.scripts.broadcast.ruleSave", "Save guideline")}
        </Button>
      </div>
    </div>
  );
}

export function BroadcastScriptsModal({
  open,
  onOpenChange,
  scripts,
  sourceSetName,
  onStarted,
  now,
}: BroadcastScriptsModalProps) {
  const { t } = useTranslation();
  const broadcast = useBroadcastScripts();
  const { data: tenantPage, isLoading: tenantsLoading } = useQuery(
    tenantPickerQueryOptions({ enabled: open }),
  );

  const [tenantIds, setTenantIds] = React.useState<string[]>([]);
  const [setName, setSetName] = React.useState(() =>
    defaultBroadcastSetName(
      sourceSetName,
      t("personaOpportunities.scripts.broadcast.defaultSetName", {
        // Hyphen, not a middle dot: SetNameSchema allows only English-safe punctuation.
        defaultValue: "Adge Top Ads - {{month}}",
        month: formatSetNameMonth(now ?? new Date()),
      }),
    ),
  );
  const [rules, setRules] = React.useState<ScriptsBroadcastGuidelineRule[]>([]);
  /** Open add/edit form; null when closed. */
  const [ruleDraft, setRuleDraft] = React.useState<RuleDraft | null>(null);
  /** Remounts the form for each add/edit so it starts from its own draft. */
  const [ruleFormKey, setRuleFormKey] = React.useState(0);
  const [expandedRules, setExpandedRules] = React.useState<ReadonlySet<number>>(new Set());
  /** Rules dropped because every script or tenant they named left the dialog. */
  const [droppedRuleCount, setDroppedRuleCount] = React.useState(0);
  // `isPending` lands a render late, so a ref blocks a double click in the same tick.
  const submitLockRef = React.useRef(false);
  // One key per open dialog. A retry while it stays open reuses the key so the
  // BFF returns the run it already started instead of creating another set.
  const idempotencyKeyRef = React.useRef<string | null>(null);
  if (open && idempotencyKeyRef.current === null) {
    idempotencyKeyRef.current = crypto.randomUUID();
  }
  if (!open && idempotencyKeyRef.current !== null) {
    idempotencyKeyRef.current = null;
  }
  /** Tenants named in a run that started nothing. The dialog stays open to show them. */
  const [nothingStartedNames, setNothingStartedNames] = React.useState<string[] | null>(null);

  const tenants = React.useMemo(
    () =>
      (tenantPage?.items ?? []).filter(
        (tenant) => !EXCLUDED_TENANT_TYPES.has(tenant.type) && !tenant.deletedAt,
      ),
    [tenantPage],
  );
  const tenantNameById = React.useMemo(
    () => new Map(tenants.map((tenant) => [tenant.id, tenant.name])),
    [tenants],
  );
  const scriptIds = React.useMemo(() => scripts.map((script) => script.id), [scripts]);
  const scriptTitleById = React.useMemo(
    () => new Map(scripts.map((script) => [script.id, script.title])),
    [scripts],
  );

  function applyPrune(nextScriptIds: readonly string[], nextTenantIds: readonly string[]) {
    const pruned = pruneGuidelineRules(rules, nextScriptIds, nextTenantIds);
    if (pruned.rules === rules) return;
    setRules(pruned.rules);
    setExpandedRules(new Set());
    if (pruned.droppedCount > 0) setDroppedRuleCount(pruned.droppedCount);
    if (ruleDraft?.index != null) {
      // A rule being edited that was dropped is saved back as a new one.
      setRuleDraft({ ...ruleDraft, index: pruned.indexMap[ruleDraft.index] ?? null });
    }
  }

  // Scripts come from the caller; fit the rules when that list changes.
  const scriptKey = scriptIds.join("\n");
  const [prunedScriptKey, setPrunedScriptKey] = React.useState(scriptKey);
  if (prunedScriptKey !== scriptKey) {
    setPrunedScriptKey(scriptKey);
    applyPrune(scriptIds, tenantIds);
  }

  function handleTenantIdsChange(next: string[]) {
    setTenantIds(next);
    applyPrune(scriptIds, next);
  }

  const isSubmitting = broadcast.isPending || submitLockRef.current;
  const trimmedSetName = setName.trim();
  // Same rule and message as the Name this set dialog; empty shows no message, only a disabled Generate.
  const setNameResult = SetNameSchema.safeParse(setName);
  const setNameError =
    trimmedSetName.length === 0 || setNameResult.success
      ? null
      : (setNameResult.error.issues[0]?.message ?? null);
  const scriptCount = scripts.length;
  const tenantCount = tenantIds.length;
  const tooManyScripts = scriptCount > SCRIPTS_BROADCAST_SCRIPTS_MAX;
  const tooManyTenants = tenantCount > SCRIPTS_BROADCAST_TENANTS_MAX;
  const rulesFull = rules.length >= SCRIPTS_BROADCAST_GUIDELINE_RULES_MAX;
  const oversizedPair = React.useMemo(
    () => findOversizedPair({ scripts, tenantIds, rules }),
    [scripts, tenantIds, rules],
  );
  const canSubmit =
    !isSubmitting &&
    scriptCount > 0 &&
    tenantCount > 0 &&
    setNameResult.success &&
    !tooManyScripts &&
    !tooManyTenants &&
    !oversizedPair &&
    // An open form holds a guideline that is not saved yet.
    ruleDraft === null;

  const requestError = broadcast.error
    ? getLocalUserFacingApiMessage(broadcast.error, passThroughMessage)
    : null;

  const limitWarning = tooManyScripts
    ? t("personaOpportunities.scripts.broadcast.tooManyScripts", {
        defaultValue: "Pick up to {{max}} scripts. Deselect some to continue.",
        max: SCRIPTS_BROADCAST_SCRIPTS_MAX,
      })
    : tooManyTenants
      ? t("personaOpportunities.scripts.broadcast.tooManyTenants", {
          defaultValue: "Pick up to {{max}} tenants per run.",
          max: SCRIPTS_BROADCAST_TENANTS_MAX,
        })
      : null;

  const summary = t("personaOpportunities.scripts.broadcast.summary", {
    defaultValue: "{{sources}} × {{tenants}} = {{total}}",
    sources: t("personaOpportunities.scripts.broadcast.summarySources", {
      count: scriptCount,
      defaultValue_one: "{{count}} script",
      defaultValue_other: "{{count}} scripts",
    }),
    tenants: t("personaOpportunities.scripts.broadcast.summaryTenants", {
      count: tenantCount,
      defaultValue_one: "{{count}} tenant",
      defaultValue_other: "{{count}} tenants",
    }),
    total: t("personaOpportunities.scripts.broadcast.summaryScripts", {
      count: scriptCount * tenantCount,
      defaultValue_one: "{{count}} script",
      defaultValue_other: "{{count}} scripts",
    }),
  });

  const scriptItems: ScopeItem[] = scripts.map((script) => ({
    id: script.id,
    label: script.title,
  }));
  const tenantItems: ScopeItem[] = tenantIds.flatMap((id) => {
    const name = tenantNameById.get(id);
    return name === undefined ? [] : [{ id, label: name }];
  });

  function scriptScopeLabel(rule: ScriptsBroadcastGuidelineRule): string {
    if (!rule.scriptIds) {
      return t("personaOpportunities.scripts.broadcast.ruleAllScripts", "All scripts");
    }
    return t("personaOpportunities.scripts.broadcast.ruleScriptCount", {
      count: rule.scriptIds.length,
      defaultValue_one: "{{count}} script",
      defaultValue_other: "{{count}} scripts",
    });
  }

  function tenantScopeLabel(rule: ScriptsBroadcastGuidelineRule): string {
    if (!rule.tenantIds) {
      return t("personaOpportunities.scripts.broadcast.ruleAllTenants", "All tenants");
    }
    const names = rule.tenantIds.map((id) => tenantNameById.get(id) ?? id);
    const shown = names.slice(0, RULE_TENANT_NAMES_SHOWN).join(", ");
    const hidden = names.length - RULE_TENANT_NAMES_SHOWN;
    if (hidden <= 0) return shown;
    return t("personaOpportunities.scripts.broadcast.ruleTenantNamesMore", {
      defaultValue: "{{names}} +{{more}}",
      names: shown,
      more: hidden,
    });
  }

  function openRuleForm(draft: RuleDraft) {
    setDroppedRuleCount(0);
    setRuleFormKey((key) => key + 1);
    setRuleDraft(draft);
  }

  function handleSaveRule(rule: ScriptsBroadcastGuidelineRule) {
    const index = ruleDraft?.index ?? null;
    setRules((current) =>
      index === null ? [...current, rule] : current.map((entry, i) => (i === index ? rule : entry)),
    );
    setRuleDraft(null);
  }

  function handleRemoveRule(index: number) {
    setDroppedRuleCount(0);
    setRules((current) => current.filter((_, i) => i !== index));
    setExpandedRules(new Set());
  }

  function toggleRuleExpanded(index: number) {
    setExpandedRules((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function handleOpenChange(next: boolean) {
    if (broadcast.isPending || submitLockRef.current) return;
    onOpenChange(next);
  }

  function handleSubmit() {
    if (!canSubmit || submitLockRef.current) return;
    const parsedSetName = SetNameSchema.safeParse(setName);
    if (!parsedSetName.success) return;
    submitLockRef.current = true;
    setNothingStartedNames(null);
    broadcast.mutate(
      {
        scriptIds,
        tenantIds,
        setName: parsedSetName.data,
        guidelineRules: rules,
        idempotencyKey: idempotencyKeyRef.current ?? crypto.randomUUID(),
      },
      {
        onSuccess: (result) => {
          const summary = summarizeScriptsBroadcast(result);
          if (broadcastStartedNothing(summary)) {
            // Every tenant was skipped or failed: keep the dialog and the selection.
            setNothingStartedNames(summary.failedTenantNames);
            return;
          }
          onStarted?.();
          onOpenChange(false);
        },
        onSettled: () => {
          submitLockRef.current = false;
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        variant="form"
        className="flex max-h-[min(90vh,760px)] w-full flex-col gap-0 overflow-hidden sm:max-w-xl"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (isSubmitting) event.preventDefault();
        }}
        data-testid="persona-broadcast-scripts-modal"
      >
        <DialogHeader className="-mx-5 -mt-5 shrink-0 gap-1 border-b border-border px-5 pb-4 pt-5 pr-12">
          <DialogTitle className="text-base font-semibold leading-snug">
            {t("personaOpportunities.scripts.broadcast.title", "Generate for tenants")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "personaOpportunities.scripts.broadcast.description",
              "Each tenant gets a new set with one adapted copy of each selected script.",
            )}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <div className="-mx-5 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-muted-foreground">
                {t("personaOpportunities.scripts.broadcast.scriptsLabel", "Scripts")}
              </span>
              <ul
                className="flex max-h-32 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-muted/40 px-3 py-2"
                aria-label={t("personaOpportunities.scripts.broadcast.scriptsLabel", "Scripts")}
              >
                {scripts.map((script) => (
                  <li key={script.id} className="truncate text-xs text-foreground">
                    {script.title}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label id="persona-broadcast-tenants-label">
                  {t("personaOpportunities.scripts.broadcast.tenantsLabel", "Tenants")}
                </Label>
                {tenants.length > 0 ? (
                  <button
                    type="button"
                    className="text-xs text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/40 disabled:opacity-50"
                    disabled={isSubmitting}
                    onClick={() => handleTenantIdsChange(tenants.map((tenant) => tenant.id))}
                  >
                    {t("personaOpportunities.scripts.broadcast.selectAllTenants", {
                      count: tenants.length,
                      defaultValue_one: "Select {{count}} tenant",
                      defaultValue_other: "Select all {{count}} tenants",
                    })}
                  </button>
                ) : null}
              </div>
              <AdminOrdersMultiSelect
                items={tenants}
                selectedIds={tenantIds}
                onChange={handleTenantIdsChange}
                getId={(tenant) => tenant.id}
                getLabel={(tenant) => tenant.name}
                renderItem={(tenant) => <OrgPickerOptionContent org={tenant} accent={false} />}
                placeholder={t(
                  "personaOpportunities.scripts.broadcast.tenantsPlaceholder",
                  "Pick tenants",
                )}
                selectedCountLabel={(count) =>
                  t("personaOpportunities.scripts.broadcast.tenantsSelected", {
                    count,
                    defaultValue_one: "{{count}} tenant",
                    defaultValue_other: "{{count}} tenants",
                  })
                }
                ariaLabel={t("personaOpportunities.scripts.broadcast.tenantsLabel", "Tenants")}
                searchPlaceholder={t(
                  "personaOpportunities.scripts.broadcast.tenantsSearch",
                  "Search tenants…",
                )}
                emptyMessage={
                  tenantsLoading
                    ? t("personaOpportunities.scripts.broadcast.tenantsLoading", "Loading tenants…")
                    : t("personaOpportunities.scripts.broadcast.tenantsEmpty", "No tenants match.")
                }
                clearLabel={t("personaOpportunities.scripts.broadcast.tenantsClear", "Clear")}
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="persona-broadcast-set-name">
                {t("personaOpportunities.scripts.broadcast.setNameLabel", "Set name")}
              </Label>
              <Input
                id="persona-broadcast-set-name"
                value={setName}
                maxLength={SCRIPTS_BROADCAST_SET_NAME_MAX_CHARS}
                disabled={isSubmitting}
                aria-invalid={setNameError ? true : undefined}
                aria-describedby={setNameError ? "persona-broadcast-set-name-error" : undefined}
                onChange={(event) => setSetName(event.target.value)}
              />
              {setNameError ? (
                <p id="persona-broadcast-set-name-error" className="text-xs text-destructive">
                  {setNameError}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  iconStart="plus"
                  disabled={isSubmitting || ruleDraft !== null || rulesFull}
                  onClick={() => openRuleForm(emptyDraft())}
                  data-testid="persona-broadcast-add-guideline"
                >
                  {t("personaOpportunities.scripts.broadcast.addGuideline", "Add guideline")}
                </Button>
                {rulesFull ? (
                  <span className="text-xs text-muted-foreground">
                    {t("personaOpportunities.scripts.broadcast.rulesMax", {
                      defaultValue: "Up to {{max}} guidelines per run.",
                      max: SCRIPTS_BROADCAST_GUIDELINE_RULES_MAX,
                    })}
                  </span>
                ) : null}
              </div>

              {ruleDraft ? (
                <GuidelineRuleForm
                  key={ruleFormKey}
                  initial={ruleDraft}
                  scriptItems={scriptItems}
                  tenantItems={tenantItems}
                  disabled={isSubmitting}
                  onSave={handleSaveRule}
                  onCancel={() => setRuleDraft(null)}
                />
              ) : null}

              {droppedRuleCount > 0 ? (
                <p
                  role="status"
                  className="flex items-start gap-1.5 text-xs text-muted-foreground"
                  data-testid="persona-broadcast-rules-dropped"
                >
                  <Icon name="info" size="xs" color="inherit" className="mt-0.5 shrink-0" />
                  {t("personaOpportunities.scripts.broadcast.rulesDropped", {
                    count: droppedRuleCount,
                    defaultValue_one:
                      "Removed {{count}} guideline. It applied only to scripts or tenants that are no longer selected.",
                    defaultValue_other:
                      "Removed {{count}} guidelines. They applied only to scripts or tenants that are no longer selected.",
                  })}
                </p>
              ) : null}

              {rules.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted-foreground">
                    {t("personaOpportunities.scripts.broadcast.guidelinesTitle", {
                      defaultValue: "Guidelines ({{count}})",
                      count: rules.length,
                    })}
                  </span>
                  <ul
                    className="flex flex-col gap-1.5"
                    aria-label={t(
                      "personaOpportunities.scripts.broadcast.guidelinesListAria",
                      "Guidelines",
                    )}
                  >
                    {rules.map((rule, index) => {
                      const expanded = expandedRules.has(index);
                      const clampable =
                        rule.text.length > RULE_TEXT_CLAMP_HINT_CHARS || rule.text.includes("\n");
                      const isEditing = ruleDraft?.index === index;
                      return (
                        <li
                          key={index}
                          className={cn(
                            "flex items-start gap-2 rounded-md border border-border px-3 py-2",
                            isEditing &&
                              "border-primary/40 bg-primary/5 dark:border-primary/60 dark:bg-primary/15",
                          )}
                          data-testid="persona-broadcast-rule"
                        >
                          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <p
                              className={cn(
                                "whitespace-pre-wrap break-words text-xs text-foreground",
                                !expanded && "line-clamp-2",
                              )}
                            >
                              {rule.text}
                            </p>
                            {clampable ? (
                              <button
                                type="button"
                                className="w-fit text-[11px] text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/40"
                                aria-expanded={expanded}
                                onClick={() => toggleRuleExpanded(index)}
                              >
                                {expanded
                                  ? t(
                                      "personaOpportunities.scripts.broadcast.ruleShowLess",
                                      "Show less",
                                    )
                                  : t(
                                      "personaOpportunities.scripts.broadcast.ruleShowMore",
                                      "Show more",
                                    )}
                              </button>
                            ) : null}
                            <div className="flex flex-wrap gap-1">
                              <Badge
                                variant="slate"
                                title={
                                  rule.scriptIds
                                    ? rule.scriptIds
                                        .map((id) => scriptTitleById.get(id) ?? id)
                                        .join(", ")
                                    : undefined
                                }
                              >
                                {scriptScopeLabel(rule)}
                              </Badge>
                              <Badge variant="slate">{tenantScopeLabel(rule)}</Badge>
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-0.5">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              iconStart="pencil"
                              disabled={isSubmitting || ruleDraft !== null}
                              aria-label={t("personaOpportunities.scripts.broadcast.ruleEdit", {
                                defaultValue: "Edit guideline {{index}}",
                                index: index + 1,
                              })}
                              onClick={() => openRuleForm(draftFromRule(rule, index))}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-xs"
                              iconStart="trash-2"
                              disabled={isSubmitting || ruleDraft !== null}
                              aria-label={t("personaOpportunities.scripts.broadcast.ruleRemove", {
                                defaultValue: "Remove guideline {{index}}",
                                index: index + 1,
                              })}
                              onClick={() => handleRemoveRule(index)}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>

            <p
              className="text-sm tabular-nums text-foreground"
              data-testid="persona-broadcast-summary"
            >
              {summary}
            </p>

            {limitWarning ? (
              <p role="status" className="text-xs text-muted-foreground">
                {limitWarning}
              </p>
            ) : null}

            {oversizedPair ? (
              <p
                role="alert"
                className="flex items-start gap-1.5 text-xs text-destructive"
                data-testid="persona-broadcast-pair-too-long"
              >
                <Icon name="alert-circle" size="xs" color="inherit" className="mt-0.5 shrink-0" />
                {t("personaOpportunities.scripts.broadcast.pairTooLong", {
                  defaultValue:
                    "“{{script}}” for {{tenant}} is too long with its guidelines ({{length}} of {{max}} characters). Shorten a guideline or apply it to fewer scripts or tenants.",
                  script:
                    scriptTitleById.get(oversizedPair.scriptId) ??
                    oversizedPair.scriptId,
                  tenant: tenantNameById.get(oversizedPair.tenantId) ?? oversizedPair.tenantId,
                  length: oversizedPair.length,
                  max: BROADCAST_PAIR_DESCRIPTION_MAX_CHARS,
                })}
              </p>
            ) : null}

            {nothingStartedNames ? (
              <p
                role="alert"
                className="flex items-start gap-1.5 text-xs text-destructive"
                data-testid="persona-broadcast-nothing-started"
              >
                <Icon name="alert-circle" size="xs" color="inherit" className="mt-0.5 shrink-0" />
                {nothingStartedNames.length > 0
                  ? t("personaOpportunities.scripts.broadcast.nothingStarted", {
                      defaultValue: "Nothing started. Could not start on: {{names}}",
                      names: nothingStartedNames.join(", "),
                    })
                  : t(
                      "personaOpportunities.scripts.broadcast.nothingStartedNoNames",
                      "Nothing started. Try again or pick other tenants.",
                    )}
              </p>
            ) : null}

            {requestError ? (
              <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive">
                <Icon name="alert-circle" size="xs" color="inherit" />
                {requestError}
              </p>
            ) : null}
          </div>

          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isSubmitting}
              onClick={() => handleOpenChange(false)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              iconStart={isSubmitting ? "loader" : "sparkles"}
              className={isSubmitting ? "[&_svg]:animate-spin" : undefined}
              disabled={!canSubmit}
              aria-busy={isSubmitting || undefined}
              data-testid="persona-broadcast-submit"
            >
              {t("personaOpportunities.scripts.broadcast.submit", "Generate")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
